import React, { useCallback, useState } from 'react'
import { Upload, X, Loader2, Image as ImageIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import {
  uploadImage,
  deleteFile,
  validateImageFile,
  ALLOWED_IMAGE_TYPES,
  MAX_FILE_SIZE,
} from '@/services/upload.service'
import { toast } from '@/hooks/use-toast'

interface ImageUploadProps {
  imageUrl?: string | null
  imageKey?: string | null
  onImageUploaded: (key: string) => void
  onImageRemoved: () => void
  folder: 'programs' | 'products'
  disabled?: boolean
  className?: string
}

export const ImageUpload: React.FC<ImageUploadProps> = ({
  imageUrl,
  imageKey,
  onImageUploaded,
  onImageRemoved,
  folder,
  disabled = false,
  className,
}) => {
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [isDragOver, setIsDragOver] = useState(false)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  const handleFile = useCallback(
    async (file: File) => {
      const validationError = validateImageFile(file)
      if (validationError) {
        toast({
          title: 'Invalid File',
          description: validationError,
          variant: 'destructive',
        })
        return
      }

      const reader = new FileReader()
      reader.onload = e => {
        setPreviewUrl(e.target?.result as string)
      }
      reader.readAsDataURL(file)

      setIsUploading(true)
      setUploadProgress(0)

      try {
        const key = await uploadImage(file, folder, progress => {
          setUploadProgress(progress)
        })

        onImageUploaded(key)
        toast({
          title: 'Image Uploaded',
          description: 'Image has been uploaded successfully.',
          variant: 'success',
        })
      } catch (error) {
        console.error('Upload error:', error)
        setPreviewUrl(null)
        toast({
          title: 'Upload Failed',
          description: error instanceof Error ? error.message : 'Failed to upload image',
          variant: 'destructive',
        })
      } finally {
        setIsUploading(false)
        setUploadProgress(0)
      }
    },
    [folder, onImageUploaded],
  )

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      setIsDragOver(false)

      if (disabled || isUploading) return

      const file = e.dataTransfer.files[0]
      if (file) {
        handleFile(file)
      }
    },
    [disabled, isUploading, handleFile],
  )

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      if (!disabled && !isUploading) {
        setIsDragOver(true)
      }
    },
    [disabled, isUploading],
  )

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragOver(false)
  }, [])

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file) {
        handleFile(file)
      }
      e.target.value = ''
    },
    [handleFile],
  )

  const handleRemove = useCallback(async () => {
    if (!imageKey || disabled || isUploading) return

    try {
      await deleteFile(imageKey)
      setPreviewUrl(null)
      onImageRemoved()
      toast({
        title: 'Image Removed',
        description: 'Image has been removed successfully.',
        variant: 'success',
      })
    } catch (error) {
      console.error('Delete error:', error)
      toast({
        title: 'Remove Failed',
        description: error instanceof Error ? error.message : 'Failed to remove image',
        variant: 'destructive',
      })
    }
  }, [imageKey, disabled, isUploading, onImageRemoved])

  const displayUrl = previewUrl || imageUrl
  const hasImage = !!displayUrl

  return (
    <div className={cn('w-full', className)}>
      <div
        className={cn(
          'relative border-2 border-dashed rounded-lg transition-colors',
          isDragOver && !disabled
            ? 'border-blue-500 bg-blue-50'
            : hasImage
              ? 'border-slate-200'
              : 'border-slate-300',
          disabled && 'opacity-50 cursor-not-allowed',
          !disabled && !hasImage && 'hover:border-slate-400 cursor-pointer',
        )}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        {hasImage ? (
          <div className="relative aspect-video">
            <img src={displayUrl} alt="Uploaded image" className="w-full h-full object-cover rounded-lg" />
            {!disabled && !isUploading && (
              <Button
                type="button"
                variant="destructive"
                size="icon"
                className="absolute top-2 right-2 h-8 w-8"
                onClick={handleRemove}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
            {isUploading && (
              <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center">
                <div className="text-center text-white">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
                  <span className="text-sm">{uploadProgress}%</span>
                </div>
              </div>
            )}
          </div>
        ) : (
          <label
            className={cn(
              'flex flex-col items-center justify-center py-8 px-4',
              !disabled && 'cursor-pointer',
            )}
          >
            <input
              type="file"
              className="hidden"
              accept={ALLOWED_IMAGE_TYPES.join(',')}
              onChange={handleFileInput}
              disabled={disabled || isUploading}
            />
            {isUploading ? (
              <>
                <Loader2 className="h-10 w-10 text-slate-400 animate-spin mb-3" />
                <span className="text-sm text-slate-600">Uploading... {uploadProgress}%</span>
              </>
            ) : (
              <>
                <div className="w-14 h-14 bg-slate-100 rounded-full flex items-center justify-center mb-3">
                  {isDragOver ? (
                    <Upload className="h-6 w-6 text-blue-500" />
                  ) : (
                    <ImageIcon className="h-6 w-6 text-slate-400" />
                  )}
                </div>
                <span className="text-sm font-medium text-slate-700 mb-1">
                  {isDragOver ? 'Drop image here' : 'Click to upload or drag and drop'}
                </span>
                <span className="text-xs text-slate-500">
                  PNG, JPG, GIF or WebP (max {MAX_FILE_SIZE / (1024 * 1024)}MB)
                </span>
              </>
            )}
          </label>
        )}
      </div>
    </div>
  )
}

export default ImageUpload
